import { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { shallowEqual } from "react-redux";
import { Accordion, SearchBar, Typography } from "components/common";
import SortAndFilterMenu from "components/common/SortAndFilterMenu/sort-and-filter-menu";
import SwitchHeader from "../SwitchHeader";
import AgentListItem from "../AgentListItem";
import DepartmentListItem, { MockDepartment } from "../DepartmentListItem";
import { AgentFilterFields } from "../AgentFilters/AgentFiltersMenu";
import AgentsAppliedFilters from "../AgentFilters/AgentsAppliedFilters";
import { RootState } from "../../../../redux/store";
import { CallCenterAgent } from "../../../../redux/slices/agents.slice";
import { useGetCallCenterAgentsStats } from "services/agents.service";
import { Utils } from "utils";
import { useAccountChange } from "hooks/useAccountChange";
import { setSelectedDepartmentIds } from "../../../../redux/slices/callcenter.slice";

export type StatusKey =
  | "Waiting"
  | "In a queue call"
  | "Receiving"
  | "Logged Out";


const AgentsDepartmentsList = () => {
  const dispatch = useDispatch();
  const agents = useSelector((state: RootState) => state.agents.items, shallowEqual);
  const departments = useSelector((state: RootState) => state.apps.data.queue || [], shallowEqual);
  const selectedDepartmentIds = useSelector((state: RootState) => state.callCenter.selectedDepartmentIds, shallowEqual);
  const [activeTab, setActiveTab] = useState<"agents" | "departments">("agents");
  const [sortType, setSortType] = useState<"state-asc" | "state-desc" | "asc" | "desc">("state-asc");
  const [departmentSortType, setDepartmentSortType] = useState<'asc' | 'desc'>('asc');
  
  const [agentSearch, setAgentSearch] = useState('');
  
  const initialAgentFilters = {
    status: null,
    queue: null,
    tier: null,
  };
  const [agentFilters, setAgentFilters] = useState<any>(initialAgentFilters);
  const [localAgentFilters, setLocalAgentFilters] = useState<any>(initialAgentFilters);

  const { data, refetch: refetchAgentsStats } = useGetCallCenterAgentsStats()
  
  const agentStatsMap = useMemo(() => {
    return Utils.arrayToHashmap(data?.data?.summary || [], 'agent_id');
  }, [data?.data])

  useAccountChange(true, refetchAgentsStats);

  useEffect(() => {
    const interval = setInterval(() => {
      refetchAgentsStats();
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setLocalAgentFilters(agentFilters || {});
  }, [agentFilters]);

  const handleAgentFilterChange = useCallback((fieldName: string, value: any) => {
    setLocalAgentFilters((prev: any) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleApplyAgentFilters = useCallback(() => {
    setAgentFilters(localAgentFilters);
  }, [localAgentFilters]);

  const handleClearAgentFilters = useCallback(() => {
    setLocalAgentFilters(initialAgentFilters);
    setAgentFilters(initialAgentFilters);
  }, []);

  const handleRemoveAgentFilter = useCallback((key: string) => {
    const updated = { ...agentFilters, [key]: null };
    setAgentFilters(updated);
    setLocalAgentFilters(updated);
  }, [agentFilters]);

  const handleAgentTreeChange = useCallback((value: string | undefined) => {
    if (!value) {
      setSortType('state-asc');
      return;
    }
    setSortType(value as "state-asc" | "state-desc" | "asc" | "desc");
  }, []);

  const agentTreeValue = useMemo(() => {
    return sortType;
  }, [sortType]);

  const agentTreeData = useMemo(() => [
    {
      title: <span className="select-tree-parent">Status</span>,
      value: 'status',
      key: 'status',
      selectable: false,
      children: [
        { title: 'Status Ascending', value: 'state-asc', key: 'state-asc' },
        { title: 'Status Descending', value: 'state-desc', key: 'state-desc' },
      ],
    },
    {
      title: <span className="select-tree-parent">Extension</span>,
      value: 'extension',
      key: 'extension',
      selectable: false,
      children: [
        { title: 'Ascending', value: 'asc', key: 'asc' },
        { title: 'Descending', value: 'desc', key: 'desc' },
      ],
    },
  ], []);

  const getAgentStateText = useCallback((agent: CallCenterAgent): string => {
    const hasNoDevices = !agent.agentDevices || agent.agentDevices.length === 0;
    if (hasNoDevices || agent.status === "Logged Out") {
      return "Logged Out";
    }
    
    if (agent.state === "Receiving") {
      return "Ringing";
    }
    
    if (agent.state === "In a queue call") {
      return "In a queue call";
    }
    
    if (agent.state === "Waiting" || agent.state === "Ready") {
      return "Available";
    }
    
    if (agent.status === "Available") {
      return "Available";
    }
    
    // Default fallback
    return agent.state || agent.status || "Unknown";
  }, []);

  const selectedDepartmentExtensions = useMemo(() => {
    if (selectedDepartmentIds.length === 0) return new Set<string>();
    return new Set(
      departments
        .filter((dept: any) => selectedDepartmentIds.includes(dept.id))
        .map((dept: any) => dept.extension)
    );
  }, [selectedDepartmentIds, departments]);

  const filteredAndSortedAgents = useMemo(() => {
    let filtered = [...agents];

    // Filter by selected departments
    if (selectedDepartmentExtensions.size > 0) {
      filtered = filtered.filter(agent => {
        return agent.queue?.some(queueExt => selectedDepartmentExtensions.has(queueExt));
      });
    }

    if (agentSearch.trim()) {
      const searchLower = agentSearch.toLowerCase().trim();
      filtered = filtered.filter(agent => {
        const name = (agent.name || '').toLowerCase();
        const extension = (agent.extension || '').toLowerCase();
        return name.includes(searchLower) || extension.includes(searchLower);
      });
    }

    if (agentFilters.status && Array.isArray(agentFilters.status) && agentFilters.status.length) {
      const statusSet = new Set(agentFilters.status);
      filtered = filtered.filter(agent => {
        const stateText = getAgentStateText(agent);
        
        if (statusSet.has('DND') && agent.dndEnabled) {
          return true;
        }
        
        if (statusSet.has('Available') && agent.dndEnabled) {
          return false;
        }
        
        return statusSet.has(stateText);
      });
    }

    if (agentFilters.queue && Array.isArray(agentFilters.queue) && agentFilters.queue.length) {
      const queueSet = new Set(agentFilters.queue);
      filtered = filtered.filter(agent => {
        return agent.queue?.some(q => queueSet.has(q));
      });
    }

    if (agentFilters.tier && Array.isArray(agentFilters.tier) && agentFilters.tier.length) {
      const tierSet = new Set(agentFilters.tier.map(String));
      filtered = filtered.filter(agent => {
        return agent.level?.some(tier => tierSet.has(String(tier)));
      });
    }

    const sorted = [...filtered];
    
    if (sortType === "state-asc" || sortType === "state-desc") {
      const STATE_ORDER: Record<string, number> = {
        "In a queue call": 0,
        "Ringing": 1,
        "Available": 2,
        "Logged Out": 3,
      };
      
      sorted.sort((a, b) => {
        const aState = getAgentStateText(a);
        const bState = getAgentStateText(b);
        const aOrder = STATE_ORDER[aState] ?? 4;
        const bOrder = STATE_ORDER[bState] ?? 4;
        const comparison = aOrder - bOrder;
        return sortType === "state-desc" ? -comparison : comparison;
      });
    } else if (sortType === "asc" || sortType === "desc") {
      const isDesc = sortType === "desc";
      sorted.sort((a, b) => {
        const extA = a.extension ? parseInt(a.extension, 10) : NaN;
        const extB = b.extension ? parseInt(b.extension, 10) : NaN;
        
        if (!isNaN(extA) && !isNaN(extB)) {
          return isDesc ? extB - extA : extA - extB;
        }
        
        if (!a.extension && !b.extension) return 0;
        if (!a.extension) return 1;
        if (!b.extension) return -1;
        
        return isDesc 
          ? b.extension.localeCompare(a.extension)
          : a.extension.localeCompare(b.extension);
      });
    }
    
    return sorted;
  }, [agents, selectedDepartmentExtensions, agentSearch, agentFilters, sortType, getAgentStateText]);

  const handleDepartmentToggle = useCallback((departmentId: string) => {
    const newSet = new Set(selectedDepartmentIds);
    if (newSet.has(departmentId)) {
      newSet.delete(departmentId);
    } else {
      newSet.add(departmentId);
    }
    dispatch(setSelectedDepartmentIds(Array.from(newSet)));
  }, [selectedDepartmentIds, dispatch]);

  const handleSelectAllDepartments = useCallback(() => {
    if (selectedDepartmentIds.length === departments.length) {
      dispatch(setSelectedDepartmentIds([]));
    } else {
      dispatch(setSelectedDepartmentIds(departments.map(dept => dept.id)));
    }
  }, [departments, selectedDepartmentIds.length, dispatch]);

  const filteredAgentsCount = useMemo(() => {
    if (selectedDepartmentExtensions.size === 0) {
      return agents.length;
    }
    
    return agents.filter(agent => {
      return agent.queue?.some(queueExt => selectedDepartmentExtensions.has(queueExt));
    }).length;
  }, [agents, selectedDepartmentExtensions]);

  // Memoize sorted departments
  const sortedDepartments = useMemo(() => {
    return [...departments].sort((a, b) => {
      if (departmentSortType === 'asc') {
        return a.name.localeCompare(b.name);
      } else {
        return b.name.localeCompare(a.name);
      }
    });
  }, [departments, departmentSortType]);

  // Memoize department data with agent counts
  const departmentsWithCounts = useMemo(() => {
    return sortedDepartments.map((department) => {
      const departmentAgents = agents.filter(agent => agent.queue?.includes(department.extension));
      
      return {
        department,
        departmentAgents,
        departObject: {
          id: department.id,
          name: department.name,
          extension: department.extension,
          agentCount: departmentAgents.length || 0,
          answeredCalls: departmentAgents.length > 0 ? departmentAgents.reduce((acc, agent) => acc + (agent.state === "In a queue call" ? 1 : 0), 0) : 0,
          waitingCalls: departmentAgents.length > 0 ? departmentAgents.reduce((acc, agent) => acc + (agent.state === "Waiting" || agent.state === "Ready" ? 1 : 0), 0) : 0,
          availableAgents: departmentAgents.length > 0 ? departmentAgents.filter(agent => (agent.state === "Waiting" || agent.state === "Ready") && agent.status === "Available" && agent.dndEnabled === false).length : 0,
        } as MockDepartment
      };
    });
  }, [sortedDepartments, agents]);

  
  return (
    <div className="departments-agents-container">
      <div>
        <SwitchHeader activeTab={activeTab} setActiveTab={setActiveTab} departmentCount={departments.length} agentsCount={filteredAgentsCount} />
      </div>
      <div className="sorting-accordion">
        <div className={`filter-section ${activeTab === "agents" ? "active" : ""}`}>
          <SortAndFilterMenu
            treeStyle={{ width: '100%'}}
            style={{ width: '100%', marginTop: '0.5rem' }}
            treeData={agentTreeData}
            treeValue={agentTreeValue}
            handleTreeChange={handleAgentTreeChange}
            onApplyFilters={handleApplyAgentFilters}
            onClear={handleClearAgentFilters}
          >
            <AgentFilterFields
              localFilters={localAgentFilters}
              handleFilterChange={handleAgentFilterChange}
            />
          </SortAndFilterMenu>
        </div>
        <div className={`filter-section ${activeTab === "departments" ? "active" : ""}`}>
          <Accordion
            labelName="Sort By"
            isTitleStatic={false}
            staticTitle="Ascending"
            theme="dark"
            style={{ marginTop: '1rem' }}
            menuItems={[
              { label: "Ascending", value: "asc" },
              { label: "Descending", value: "desc" }
            ]}
            selectedTitle={departmentSortType}
            onSelectedTitleChange={(val) => {
              setDepartmentSortType(val as 'asc' | 'desc');
            }}
          />
        </div>
      </div>
      <div className={`filter-actions ${activeTab === "agents" ? "active" : ""}`}>
        {(agentFilters.status || agentFilters.queue || agentFilters.tier) && (
          <div style={{ paddingInline: '1rem' }}>
            <AgentsAppliedFilters
              filters={agentFilters}
              handleClearFilters={handleClearAgentFilters}
              handleRemoveFilter={handleRemoveAgentFilter}
              onFilterUpdate={(key: string, value: any) => {
                setAgentFilters((prev: any) => ({ ...prev, [key]: value }));
                setLocalAgentFilters((prev: any) => ({ ...prev, [key]: value }));
              }}
            />
          </div>
        )}
        <div style={{ paddingInline: '1rem', paddingBottom: '1rem' }}>
          <SearchBar 
            value={agentSearch} 
            onChange={(e) => setAgentSearch(e.target.value)} 
          />
        </div>
      </div>
      <div className="tab-content-wrapper">
        <div className={`tab-content ${activeTab === "agents" ? "active" : ""}`}>
          <ul className="list-container" key={sortType}>
            {filteredAndSortedAgents.length > 0 ? (
              filteredAndSortedAgents.map((agent) => (
                <li key={agent.id} className="list-item-container new-agent-item-container">
                  <AgentListItem 
                    agent={agent} 
                    totalAnsweredCalls={agentStatsMap[agent?.id]?.total_answered} 
                    stateText={getAgentStateText(agent)} 
                  />
                </li>
              ))
            ) : (
              <div className="empty-list-message">
                <Typography variant="subHeading-2" style={{ textAlign: "center", marginTop: "1rem" }}>
                  No agents found
                </Typography>
              </div>
            )}
          </ul>
        </div>
        <div className={`tab-content ${activeTab === "departments" ? "active" : ""}`}>
          {departments.length > 0 && (
            <div style={{ paddingInline: '1rem', marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="small-text-4">
                {selectedDepartmentIds.length} of {departments.length} selected
              </Typography>
              <button
                onClick={handleSelectAllDepartments}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#1890ff',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  textDecoration: 'underline'
                }}
              >
                {selectedDepartmentIds.length === departments.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
          )}
          <ul className="list-container">
            {departmentsWithCounts.length > 0 ? (
              departmentsWithCounts.map(({ departObject, department }) => (
                <li 
                  key={department.id} 
                  className="list-item-container new-department-item-container"
                  onClick={() => handleDepartmentToggle(department.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <DepartmentListItem 
                    department={departObject} 
                    isSelected={selectedDepartmentIds.includes(department.id)}
                    onToggle={() => handleDepartmentToggle(department.id)}
                  />
                </li>
              ))
            ) : (
              <div className="empty-list-message">
                <Typography variant="subHeading-2" style={{ textAlign: "center", marginTop: "1rem" }}>
                  No departments found
                </Typography>
              </div>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};


export default AgentsDepartmentsList;
